import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  SquaresFour,
  Plugs,
  ListChecks,
  GearSix,
  SignOut,
  Plus,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: SquaresFour, testid: "nav-dashboard" },
  { to: "/connections", label: "Connections", icon: Plugs, testid: "nav-connections" },
  { to: "/history", label: "History", icon: ListChecks, testid: "nav-history" },
  { to: "/settings", label: "Settings", icon: GearSix, testid: "nav-settings" },
];

export default function AppLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch((e) => console.error("auth/me failed in sidebar", e));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[#f4f4f0]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-[#0A0A0A] text-neutral-100 flex flex-col">
        <div className="p-6 border-b border-neutral-900">
          <div className="brand-mark text-xl" data-testid="sidebar-brand">
            <img src="/brand/logo-orangeburst.webp" alt="QuickSuite" />
            <span className="divider" />
            <span className="sub text-neutral-100">Connect</span>
          </div>
        </div>

        <div className="p-4">
          <Button
            data-testid="sidebar-new-connection"
            onClick={() => navigate("/connections/new")}
            className="w-full bg-[#F58021] hover:bg-[#D6691A] text-white gap-2 rounded-md h-11 font-semibold"
          >
            <Plus weight="bold" size={16} /> New connection
          </Button>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-white text-black" : "text-neutral-300 hover:bg-neutral-900 hover:text-white"
                }`
              }
            >
              <Icon weight="duotone" size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-neutral-900">
          {user && (
            <div className="flex items-center gap-3 mb-3" data-testid="sidebar-user">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-neutral-700" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-neutral-800 grid place-items-center text-sm">
                  {user.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{user.name}</div>
                <div className="text-xs text-neutral-500 truncate">{user.email}</div>
              </div>
            </div>
          )}
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-2 text-sm text-neutral-400 hover:text-white px-1 py-1.5 transition-colors"
          >
            <SignOut weight="bold" size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
